import { FormComponent } from '@synergy/abstract';
import { FormikProps } from 'formik';

export interface FormControlledStepComponentProps<T> {
    setCanActivate: (canActivate: () => boolean) => void;
    setTryToActivate: (tryToActivate: () => void) => void;
    setFirstComponentWillMountTriggered: () => void;
    // Cannot type state in superclass
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    propagateInternalState: (state: T) => void;
    // data can be of any type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onCtaButtonChanged: (enabled: boolean, data: any) => void;
    internalState?: T;
    /**
     * The document as the server has it. internalState may sit on top of it once
     * the step has handed values to the wizard, so this — not internalState — is
     * what the unsaved marker compares against.
     */
    documentState?: T;
    globalState?: T;
    firstComponentWillMountTriggered: boolean;
    unlocked?: boolean;
}

// Generics needed for common interface with abstract base class
/* eslint-disable @typescript-eslint/no-unused-vars */
export interface FormControlledStepComponent<
    T,
    FormValues,
    P extends FormControlledStepComponentProps<T> = FormControlledStepComponentProps<T>,
    S = Record<string, unknown>
> {
    firstComponentWillMount(): void;
}
/* eslint-enable */

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export abstract class FormControlledStepComponent<
        T,
        FormValues,
        P extends FormControlledStepComponentProps<T> = FormControlledStepComponentProps<T>,
        S = Record<string, unknown>
    >
    extends FormComponent<FormValues, P, S>
    implements FormControlledStepComponent<T, FormValues, P, S>
{
    /**
     * @throws if this is not set in the render method to the reference of the formik element
     */
    protected formikInstance: FormikProps<FormValues> | null = null;

    protected abstract initialValues: FormValues;
    protected abstract onSubmit: (values: FormValues) => void;

    protected tryToActivate = async () => {
        // Steps might be tried to activate at points where formikInstance is not referenced yet, prevent this
        if (this.formikInstance) {
            await this.formikInstance.submitForm();
        }
    };

    protected canActivate = () => {
        return !this.formikInstance || this.formikInstance.isValid;
    };

    protected getInitialCtaButtonState(): boolean {
        return true;
    }

    // Data can be of any type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected getInitialCtaButtonData(): any {
        return undefined;
    }

    public UNSAFE_componentWillMount() {
        this.props.setCanActivate(this.canActivate);
        this.props.setTryToActivate(this.tryToActivate);
        this.props.onCtaButtonChanged(this.getInitialCtaButtonState(), this.getInitialCtaButtonData());

        if (
            !this.props.firstComponentWillMountTriggered &&
            this.firstComponentWillMount &&
            typeof this.firstComponentWillMount === 'function'
        ) {
            this.firstComponentWillMount();
            this.props.setFirstComponentWillMountTriggered();
        }
    }
}
